import * as React from 'react';

import { Button, SearchField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideX } from 'lucide-react';

import * as state from '../../_state/brokeredLoadsList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

type BrokeredLoadListFiltersProps = {
  clearAllFiltersRef?: React.RefObject<(() => void) | null>;
};

export const BrokeredLoadListFilters: React.FC<
  BrokeredLoadListFiltersProps
> = ({ clearAllFiltersRef }) => {
  const t = useTranslations();

  // Atoms
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const setOffset = useSetAtom(state.$offset);
  const hasFiltersActive = useAtomValue(state.$filtersActive);

  // Callbacks
  const handleClearAllFilters = React.useCallback(() => {
    setSearchTerm('');
    setOffset(0);
  }, [setSearchTerm, setOffset]);

  // Effects
  /** Exposes clear-all handler to parent for empty-results panel action. */
  React.useEffect(() => {
    if (clearAllFiltersRef) {
      clearAllFiltersRef.current = handleClearAllFilters;
    }
    return () => {
      if (clearAllFiltersRef) {
        clearAllFiltersRef.current = null;
      }
    };
  }, [clearAllFiltersRef, handleClearAllFilters]);

  return (
    <>
      <SearchField
        autoFocus
        aria-label={t.brokerage.loads.filters.search.aria}
        placeholder={t.brokerage.loads.filters.search.placeholder}
        value={searchTerm}
        onChange={(value) => setSearchTerm(value ?? '')}
        className="w-[320px]"
      />
      {hasFiltersActive && (
        <Button
          variant="text"
          color="primary"
          icon={<LucideX />}
          onPress={handleClearAllFilters}
        >
          {t.brokerage.loads.filters.clearAll}
        </Button>
      )}
    </>
  );
};
